import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, AlertCircle, CheckCircle2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function Ultradash() {
  const qc = useQueryClient();
  const sessionQ = useQuery({
    queryKey: ["ultradash", "session"],
    queryFn: () => api.ultradashSession(),
    refetchInterval: 60_000,
  });
  const dashboardQ = useQuery({
    queryKey: ["ultradash", "dashboard"],
    queryFn: () => api.ultradashDashboard(),
    enabled: !!sessionQ.data?.active,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Ultradash AI Chat</h1>
        <p className="text-sm text-muted-foreground">
          Kitt-as-creator in club DMs. Session, attribution split, holdout group.
        </p>
      </header>

      {sessionQ.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : sessionQ.data?.active ? (
        <SessionCard session={sessionQ.data} />
      ) : (
        <AuthCard onAuthed={() => sessionQ.refetch()} />
      )}

      {sessionQ.data?.active && (
        <section>
          <h2 className="mb-2 text-sm font-medium">Per-creator activity (7d)</h2>
          {dashboardQ.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !dashboardQ.data?.by_creator?.length ? (
            <Empty title="No Ultradash activity yet." hint="Once Kitt sends or sees DMs in a club, rows will appear here." />
          ) : (
            <div className="overflow-hidden rounded-md border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-2xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Club</th>
                    <th className="px-3 py-1.5 text-right">Outbound 7d</th>
                    <th className="px-3 py-1.5 text-right">AI %</th>
                    <th className="px-3 py-1.5 text-right">Organic $</th>
                    <th className="px-3 py-1.5 text-right">AI-init $</th>
                    <th className="px-3 py-1.5 text-right">Hybrid $</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dashboardQ.data.by_creator.map((r) => {
                    const ai = r.ai_msgs_7d ?? 0;
                    const total = r.outbound_msgs_7d ?? 0;
                    const aiPct = total ? (ai / total) * 100 : 0;
                    return (
                      <tr key={r.club_id}>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.club_id}</td>
                        <td className="px-3 py-1.5 text-right tnum">{total}</td>
                        <td className="px-3 py-1.5 text-right tnum">
                          <span className={aiPct < 50 ? "text-amber-700" : "text-emerald-700"}>
                            {aiPct.toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right tnum">${((r.organic_cents_7d ?? 0) / 100).toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right tnum">${((r.ai_cents_7d ?? 0) / 100).toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right tnum">${((r.hybrid_cents_7d ?? 0) / 100).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {sessionQ.data?.active && (
        <section>
          <h2 className="mb-2 text-sm font-medium">Holdout groups</h2>
          {(dashboardQ.data?.holdouts ?? []).length === 0 ? (
            <Empty title="No holdouts assigned." hint="Add 5% of fans per club via /admin/api/ultradash/holdouts to enable causal lift attribution." />
          ) : (
            <div className="grid gap-2 md:grid-cols-3">
              {(dashboardQ.data?.holdouts ?? []).map((h) => (
                <div key={h.club_id} className="rounded-md border bg-card p-3">
                  <div className="text-2xs uppercase text-muted-foreground">{h.club_id}</div>
                  <div className="text-xl font-semibold tnum">{h.n_holdouts}</div>
                  <div className="text-xs text-muted-foreground">members in holdout</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function SessionCard({ session }: { session: any }) {
  return (
    <div className="rounded-lg border border-emerald-300 bg-emerald-50/40 p-4">
      <div className="flex items-center gap-2 text-sm">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <span className="font-medium">Session active</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-2xs uppercase text-muted-foreground">Email</div>
          <div className="font-mono">{session.email}</div>
        </div>
        <div>
          <div className="text-2xs uppercase text-muted-foreground">Role</div>
          <div className="font-mono">{session.organization_role || "—"}</div>
        </div>
        <div className="col-span-2">
          <div className="text-2xs uppercase text-muted-foreground">Assigned clubs</div>
          <div className="font-mono">
            {session.assigned_club_ids?.length ?? 0} club(s):{" "}
            {(session.assigned_club_ids ?? []).slice(0, 3).join(", ")}
            {(session.assigned_club_ids ?? []).length > 3 ? "…" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthCard({ onAuthed }: { onAuthed: () => void }) {
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");
  const [step, setStep] = React.useState<"enter_email" | "enter_code" | "done">("enter_email");
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSendCode = async () => {
    if (!email) return;
    setPending(true);
    setError(null);
    try {
      await api.ultradashAuthCode(email);
      setStep("enter_code");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPending(false);
    }
  };
  const onVerify = async () => {
    if (!email || !code) return;
    setPending(true);
    setError(null);
    try {
      await api.ultradashAuthVerify(email, code);
      setStep("done");
      onAuthed();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/40 p-4 space-y-3 max-w-md">
      <div className="flex items-center gap-2 text-sm">
        <Lock className="h-4 w-4 text-amber-700" />
        <span className="font-medium">No active Ultradash session</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Authenticate once. Kitt's worker pool will reuse the session for all club work.
      </p>
      {step === "enter_email" && (
        <>
          <Input
            placeholder="kitt@ultraviolet.club"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
          />
          <Button onClick={onSendCode} disabled={!email || pending} size="sm">
            {pending ? "Sending…" : "Email me a code"}
          </Button>
        </>
      )}
      {step === "enter_code" && (
        <>
          <p className="text-xs">Code sent to {email}. Enter the 6-digit code:</p>
          <Input
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
          />
          <div className="flex gap-2">
            <Button onClick={onVerify} disabled={!code || pending} size="sm">
              {pending ? "Verifying…" : "Verify"}
            </Button>
            <Button onClick={() => { setStep("enter_email"); setCode(""); }} variant="outline" size="sm">
              Back
            </Button>
          </div>
        </>
      )}
      {error && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      )}
    </div>
  );
}
